// src/hooks/common/useRouteCleanup.ts

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook reutilizable para ejecutar lógica de limpieza cuando el usuario
 * abandona una ruta específica.
 *
 * Este hook se basa en:
 * - Comparar la ruta anterior (`prevPath`) con una función de coincidencia (`matchFn`)
 * - Detectar si la nueva ruta ya no coincide con el destino original
 * - Ejecutar una función de limpieza (`onLeave`) solo si corresponde
 *
 * @param matchFn - Función que evalúa si una ruta dada pertenece a una sección específica (por ejemplo: `path.startsWith('/purchase-register')`)
 * @param onLeave - Función a ejecutar cuando se detecta que el usuario ha salido de la ruta objetivo
 * @param shouldClean - Función opcional que condiciona si realmente se debe ejecutar la limpieza. Por defecto, retorna `true`
 *
 * ✅ Ejemplo de uso:
 * ```ts
 * useRouteCleanup({
 *   matchFn: (path) => path.startsWith('/purchase-register'),
 *   onLeave: () => dispatch(resetPurchaseForm()),
 *   shouldClean: () => !purchaseSaved && !purchaseLoaded,
 * });
 * ```
 */
export const useRouteCleanup = ({
  matchFn,
  onLeave,
  shouldClean = () => true,
}: {
  matchFn: (path: string) => boolean; // ✅ Función que evalúa si una ruta pertenece al módulo
  onLeave: () => void;               // ✅ Acción a ejecutar al salir de la ruta
  shouldClean?: () => boolean;       // ✅ Condicional opcional para validar si ejecutar limpieza
}) => {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const wasOnTarget = matchFn(prevPath.current);
    const isLeavingTarget = !matchFn(location.pathname); // ✅ Ya no estamos en la ruta destino

    if (wasOnTarget && isLeavingTarget && shouldClean()) {
      onLeave(); // 🧹 Ejecutamos limpieza solo si todo se cumple
    }

    // Actualizamos la ruta previa para la próxima comparación
    prevPath.current = location.pathname;
  }, [location.pathname]);
};
